// src/components/ErrorBoundary.jsx
//
// Top-level safety net: catches render/lifecycle errors anywhere in the tree and
// shows a recoverable fallback instead of a white screen. Wrapped around the app
// in index.js. The fallback is clickable to reveal the full error message,
// stack, and React component stack, with a "Copy details" button for bug
// reports. It also forwards to the central error bus (the seam to Sentry /
// Cloud Logging later).
import React from 'react';
import '../css/Feedback.css';
import { reportError } from '../notifications/errorBus';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, componentStack: null, showDetails: false, copied: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        this.setState({ componentStack: info?.componentStack || null });
        // Centralized place to forward to an external monitor later.
        console.error('Uncaught error in component tree:', error, info?.componentStack);
        // Record it on the bus too (the on-screen center is unmounted while this
        // fallback is showing, but this keeps a single reporting seam).
        reportError(error, { source: 'react', title: 'Application error' });
    }

    handleReload = () => {
        this.setState({ hasError: false, error: null, componentStack: null });
        window.location.reload();
    };

    handleCopy = async () => {
        const { error, componentStack } = this.state;
        const text = [
            `[ERROR] ${error?.name || 'Error'}: ${error?.message || String(error)}`,
            '',
            error?.stack ? `Stack:\n${error.stack}` : '',
            componentStack ? `\nComponent stack:${componentStack}` : '',
        ]
            .filter(Boolean)
            .join('\n');
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            /* clipboard may be unavailable; ignore */
        }
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 1500);
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const { error, componentStack, showDetails, copied } = this.state;

        return (
            <div className="app-error-boundary" role="alert">
                <div className="app-error-card">
                    <div className="app-error-icon" aria-hidden="true">⚠️</div>
                    <h1>Something went wrong</h1>
                    <p>QDex hit an unexpected error. Your data is safe — reloading usually fixes it.</p>

                    {error?.message && <pre className="app-error-detail">{String(error.message)}</pre>}

                    <div className="app-error-actions">
                        <button className="app-error-btn" onClick={this.handleReload}>
                            Reload QDex
                        </button>
                        {(error?.stack || componentStack) && (
                            <button
                                className="app-error-btn app-error-btn--ghost"
                                onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
                                aria-expanded={showDetails}
                            >
                                {showDetails ? 'Hide details' : 'Show details'}
                            </button>
                        )}
                    </div>

                    {showDetails && (error?.stack || componentStack) && (
                        <div className="app-error-trace">
                            {error?.stack && <pre className="app-error-detail">{error.stack}</pre>}
                            {componentStack && (
                                <pre className="app-error-detail">{`Component stack:${componentStack}`}</pre>
                            )}
                            <button className="app-error-btn app-error-btn--ghost" onClick={this.handleCopy}>
                                {copied ? 'Copied ✓' : 'Copy details'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
